import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ListEventView, {
  FilterListEventFormValues,
} from '~/components/Shared/Event/List';
import { DEFAULT_PER_PAGE, NOTIFY_TYPE, SortType } from '~/constants/common';
import {
  EventOrderByInput,
  EventWhereInput,
  QueryMode,
  SortOrder,
} from '~/models/common';
import { fetchListEvent, postDeleteEvent } from '~/store/slices/event';
import { AppDispatch } from '~/store/store';
import { handleError, notify } from '~/utils/utils';

interface IProps {
  isAdmin?: boolean;
}

const ListEvent = ({ isAdmin }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const [filter, setFilter] = React.useState<EventWhereInput>({});
  const [sort, setSort] = React.useState<EventOrderByInput>({
    createdAt: SortOrder.Desc,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const currentPage = searchParams.get('page') || 1;

  const handleChangePage = (page: number): void => {
    searchParams.set('page', page.toString());
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeFilter = (values: FilterListEventFormValues): void => {
    searchParams.set('page', '1');
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const { name, schedule, createdAt, companyId, companyName } = values;
    setFilter({
      name: name
        ? {
            contains: name,
            mode: QueryMode.Insensitive,
          }
        : undefined,
      createdAt:
        createdAt && createdAt[0] && createdAt[1]
          ? {
              gte: dayjs(createdAt[0]).startOf('date').toDate(),
              lte: dayjs(createdAt[1]).endOf('date').toDate(),
            }
          : undefined,
      startDate: schedule?.[0]
        ? {
            gte: dayjs(schedule[0]).startOf('date').toDate(),
          }
        : undefined,
      endDate: schedule?.[1]
        ? {
            lte: dayjs(schedule[1]).endOf('date').toDate(),
          }
        : undefined,
      company:
        isAdmin && (companyId || companyName)
          ? {
              is: {
                id: companyId
                  ? {
                      contains: companyId,
                      mode: QueryMode.Insensitive,
                    }
                  : undefined,
                name: companyName
                  ? {
                      contains: companyName,
                      mode: QueryMode.Insensitive,
                    }
                  : undefined,
              },
            }
          : undefined,
    });
  };

  const handleSort = ({ columnKey, order }): void => {
    if (columnKey === undefined) {
      return;
    }

    if (columnKey === 'eventUsers') {
      setSort({
        eventUsers: order
          ? {
              _count: SortType[order] || undefined,
            }
          : undefined,
      });
    } else {
      setSort({
        [columnKey]: SortType[order] || undefined,
      });
    }
  };

  const handleDelete = async (eventId: string): Promise<void> => {
    const result: any = await dispatch(postDeleteEvent({ eventId }));
    if (!result?.error) {
      notify(NOTIFY_TYPE.SUCCESS, t('event.list.delete-success')!);
      setFilter({ ...filter });
    } else {
      handleError(result, t('event.list.delete-fail'));
    }
  };

  useEffect(() => {
    dispatch(
      fetchListEvent({
        pagination: { page: Number(currentPage), perPage: DEFAULT_PER_PAGE },
        where: filter,
        orderBy: sort,
      }),
    ).then((res: any) => {
      if (res?.error) {
        handleError(res, t('common.error'));
      }
    });
  }, [currentPage, filter, sort]);

  return (
    <ListEventView
      handleChangePage={handleChangePage}
      handleFilter={handleChangeFilter}
      handleDelete={handleDelete}
      handleSort={handleSort}
      isAdmin={isAdmin}
    />
  );
};

export default ListEvent;
